/**
 * Timezone utility functions that work on both client and server
 */

/**
 * Formats a UTC Date object in a specific timezone
 */
export function formatInTimezone(
	date: Date,
	timezone: string,
	options: Intl.DateTimeFormatOptions = {},
): string {
	const defaultOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		timeZone: timezone,
	}

	return new Intl.DateTimeFormat('en-US', {
		...defaultOptions,
		...options,
	}).format(date)
}

/**
 * Gets the timezone abbreviation for a given timezone at a specific date
 * Returns the short form like "CET", "EST", etc.
 */
export function getTimezoneAbbreviation(
	timezone: string,
	date: Date = new Date(),
): string {
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone: timezone,
		timeZoneName: 'short',
	})

	const parts = formatter.formatToParts(date)
	const timeZonePart = parts.find((part) => part.type === 'timeZoneName')

	return timeZonePart?.value || timezone
}

/**
 * Formats a date range for display in a specific timezone
 */
export function formatDateRangeInTimezone(
	startDate: Date,
	endDate: Date,
	timezone: string,
): {
	date: string
	time: string
	full: string
	timezone: string
	timezoneAbbr: string
} {
	const dateOptions: Intl.DateTimeFormatOptions = {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		timeZone: timezone,
	}

	const timeOptions: Intl.DateTimeFormatOptions = {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: timezone,
	}

	const date = formatInTimezone(startDate, timezone, dateOptions)
	const startTime = formatInTimezone(startDate, timezone, timeOptions)
	const endTime = formatInTimezone(endDate, timezone, timeOptions)
	const timezoneAbbr = getTimezoneAbbreviation(timezone, startDate)

	return {
		date,
		time: `${startTime} - ${endTime}`,
		full: `${date} from ${startTime} to ${endTime} ${timezoneAbbr}`,
		timezone,
		timezoneAbbr,
	}
}

/**
 * Checks if a given string is a valid IANA timezone identifier
 */
export function isValidTimezone(timezone: string): boolean {
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: timezone })
		return true
	} catch {
		return false
	}
}
